import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useAuth } from '../global/contexts/auth/auth';
import {
    IconEye,
    IconEyeOff,
    IconLock,
    IconUser,
} from '../common/icons';

import './styles/login.css';

// TSS vite/05 — client login (public channel).
// POST public/auth/login sets the httpOnly client_auth_token + client_csrf_token
// cookies (client-namespaced, [0073]/Q-197 — isolated from the staff channel).
// Contract: identifier (email o username, case-insensitive) + password.
// 401 is identical for user-not-found / disabled / wrong password (no enumeration).

// The landing after auth — the role-adaptive home (shows parcerías and/or
// cowhousing per the client's roles_with_data), not a single fixed view.
const HOME = '/';

export default function LoginPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { login, isAuthenticated } = useAuth();

    const [identifier, setIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (isAuthenticated) navigate(HOME, { replace: true });
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (params.get('logout') === 'true') toast.success('Sesión cerrada');
    }, [params]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!identifier || !password) return;
        setSubmitting(true);
        try {
            await login({ identifier, password });
            navigate(HOME, { replace: true });
        } catch (err) {
            // 429 (loginLimiter: 3 fallos/60s) surfaces its own message.
            toast.error(err?.message || 'Credenciales inválidas');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="login">
            <div className="login__panel">
                <aside className="login__hero">
                    <DecorNetwork />

                    <div className="login__brand login__brand--hero">
                        <img className="login__logo" src="/cowre.png" alt="" aria-hidden="true" />
                        <span className="login__wordmark">COWRE</span>
                        <span className="login__tagline">Portal de clientes</span>
                    </div>
                </aside>

                <main className="login__main">
                    <form className="login__card" onSubmit={handleSubmit}>
                        <div className="login__brand login__brand--compact">
                            <span className="login__logo-tile">
                                <img className="login__logo login__logo--sm" src="/cowre.png" alt="" aria-hidden="true" />
                            </span>
                            <span className="login__wordmark login__wordmark--compact">COWRE</span>
                        </div>

                        <header className="login__head">
                            <h2 className="login__title">Iniciar sesión</h2>
                            <p className="login__hint">Ingresa para ver tus parcerías.</p>
                        </header>

                        <label className="login__field">
                            <span className="login__label">Correo o usuario</span>
                            <span className="login__control">
                                <span className="login__control-icon"><IconUser /></span>
                                <input
                                    type="text"
                                    value={identifier}
                                    onChange={(e) => setIdentifier(e.target.value)}
                                    autoComplete="username"
                                    placeholder="nombre@finca.com"
                                    disabled={submitting}
                                    autoFocus
                                    required
                                />
                            </span>
                        </label>

                        <label className="login__field">
                            <span className="login__label">Contraseña</span>
                            <span className="login__control">
                                <span className="login__control-icon"><IconLock /></span>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    placeholder="••••••••••"
                                    disabled={submitting}
                                    required
                                />
                                <button
                                    type="button"
                                    className="login__toggle"
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <IconEyeOff /> : <IconEye />}
                                </button>
                            </span>
                        </label>

                        <button
                            type="submit"
                            className="login__submit"
                            disabled={submitting || !identifier || !password}
                        >
                            {submitting ? 'Ingresando…' : 'Ingresar'}
                        </button>

                        <p className="login__legal">
                            Al ingresar aceptas nuestra{' '}
                            <Link to="/legal">Política de privacidad y términos</Link>.
                        </p>
                    </form>
                </main>
            </div>
        </div>
    );
}

// Decorative connected-node lattice — evokes a lineage / genetic network.
// Lines carry a flowing "current" (animated dash) and nodes pulse; the whole
// group drifts slowly. Purely ornamental; hidden from assistive tech and
// disabled under prefers-reduced-motion (see login.css).
const DECOR_LINKS = [
    'M40 80 L150 140 L120 280 L260 220 L320 360 L200 440 L90 400',
    'M150 140 L300 90 L260 220',
    'M200 440 L330 520 L260 220',
    'M120 280 L90 400',
    'M40 80 L120 280',
    'M300 90 L360 230 L320 360',
];
const DECOR_NODES = [
    [40, 80, 4], [150, 140, 5], [300, 90, 4], [120, 280, 6], [260, 220, 5],
    [320, 360, 4], [90, 400, 4], [200, 440, 6], [330, 520, 5], [360, 230, 4],
    [70, 200, 3], [240, 330, 3],
];

function DecorNetwork() {
    return (
        <svg className="login__decor" viewBox="0 0 400 600" fill="none" aria-hidden="true" preserveAspectRatio="xMidYMid slice">
            <g className="login__decor-drift">
                <g className="login__decor-links" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    {DECOR_LINKS.map((d, i) => (
                        <path key={i} className="login__decor-link" d={d} />
                    ))}
                </g>
                <g className="login__decor-nodes" fill="#ffffff">
                    {DECOR_NODES.map(([cx, cy, r], i) => (
                        <circle key={i} className="login__decor-node" cx={cx} cy={cy} r={r} />
                    ))}
                </g>
            </g>
        </svg>
    );
}
